import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { DartMath } from '../../utility/dart-math.js';
import { IllegalOperationException } from '../../exceptions/illegal-operation-exception.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { Country } from './country.js';
import { Currency, getCurrencyOfCountry, getCurrencySymbol } from './currency.js';

export type PriceFormat = 'A' | '$A' | 'D' | '$D' | 'F' | '$F';

function appendCurrencySymbol(currency: Currency, formattedPrice: string): string {
  switch(currency) {
    case Currency.EUR:
      return `${getCurrencySymbol(currency)} ${formattedPrice}`;
    case Currency.USD:
      return `${getCurrencySymbol(currency)} ${formattedPrice}`;
    case Currency.HUF:
      return `${formattedPrice} ${getCurrencySymbol(currency)}`;
    case Currency.RON:
      return `${formattedPrice} ${getCurrencySymbol(currency)}`;
  }
}

// Strip the decimals from the price because HUF doesn't support decimals.
function stripDecimalsIfNecessary(currency: Currency, value: number, coins: number): number {
  return currency === Currency.HUF ? DartMath.toInt(value / coins) * coins : value;
}

export class Price implements ICloneable, IDataComparable {
  private static readonly encodedPriceMatcher: RegExp = new RegExp('^([A-Z]+):-*([0-9]+)$');

  private readonly _currency: Currency;
  private readonly _value: number;
  private readonly _coins: number;

  private constructor(
      currency: Currency,
      value: number,
      coins: number,
      skipSanitization?: boolean,
  ) {
    this._currency = currency;
    this._coins = coins;
    this._value = skipSanitization !== true ? stripDecimalsIfNecessary(currency, value, coins) : value;
  }

  static withCurrencyFromUnits(currency: Currency, amount: number): Price {
    const coins = getCoinsPerCurrencyUnit(currency);
    return new Price(currency, amount, coins);
  }

  static withCurrencyOfCountryFromUnits(country: Country, amount: number): Price {
    const currency = getCurrencyOfCountry(country);
    const coins = getCoinsPerCurrencyUnit(currency);

    return new Price(currency, amount, coins);
  }

  static withCurrencyFromAmount(currency: Currency, amount: number): Price {
    const coins = getCoinsPerCurrencyUnit(currency);
    return new Price(currency, DartMath.round(amount * coins), coins);
  }

  static withCurrencyOfCountryFromAmount(country: Country, amount: number): Price {
    const currency = getCurrencyOfCountry(country);
    const coins = getCoinsPerCurrencyUnit(currency);

    return new Price(currency, DartMath.round(amount * coins), coins);
  }

  static zero(currency: Currency): Price {
    return Price.withCurrencyFromUnits(currency, 0);
  }

  static zeroWithCurrencyOfCountry(country: Country): Price {
    return Price.withCurrencyOfCountryFromUnits(country, 0);
  }

  static decodeOrNull(encodedPrice: string | null): Price | null {
    if(isNullOrUndefined(encodedPrice)) {
      return null;
    }

    return Price.decode(encodedPrice);
  }

  static decode(encodedPrice: string): Price {
    if(encodedPrice !== null && encodedPrice.length > 0) {
      const matches = encodedPrice.match(this.encodedPriceMatcher);
      if(matches && matches.length > 2) {
        const currency: Currency = <Currency>matches[1];
        if(currency) {
          const amount = parseInt(matches[2]);
          return new Price(currency, amount, getCoinsPerCurrencyUnit(currency), true);
        }
      }
    }

    throw new Error(`Invalid encoded price: ${encodedPrice}`);
  }

  get currency(): Currency {
    return this._currency;
  }

  get value(): number {
    return this._value;
  }

  get convertToCents(): number {
    return this._value;
  }

  get convertToDecimal(): number {
    return Math.round(((this._value / this._coins) + Number.EPSILON) * 100) / 100;
  }

  /**
   * Use only for display purposes.
   * Do not create new {@link Price} instances using the value returned by this getter.
   */
  get convertToDouble(): number {
    return this._value / this._coins;
  }

  /**
   * Use only for display purposes.
   * Do not create new {@link Price} instances using the value returned by this getter.
   */
  get convertToInt(): number {
    return DartMath.toInt(this._value / this._coins);
  }

  format(format?: PriceFormat): string {
    format = format ?? '$A';

    switch(format) {
      case 'F':
        return this.convertToInt.toFixed(0);
      case '$F':
        return appendCurrencySymbol(this._currency, this.convertToInt.toFixed(0));
      case 'D':
        return this.convertToDouble.toFixed(2);
      case '$D':
        return appendCurrencySymbol(this._currency, this.convertToDouble.toFixed(2));
      case 'A':
        return this.currency == Currency.HUF ? this.convertToInt.toFixed(0) : this.convertToDouble.toFixed(2);
      case '$A':
      default:
        return this.currency == Currency.HUF ?
            appendCurrencySymbol(this._currency, this.convertToInt.toFixed(0)):
            appendCurrencySymbol(this._currency, this.convertToDouble.toFixed(2));
    }
  }

  encode(): string {
    return `${this._currency}:${this._value}`;
  }

  clone(): this {
    return new Price(this._currency, this._value, this._coins) as this;
  }

  isEqualTo(other: Price): boolean {
    if(other.currency !== this.currency) {
      throw new IllegalOperationException('You cannot add two prices with different currencies');
    }

    if(this._value < other._value) return false;
    else if(this._value > other._value) return false;
    else return true;
  }

  compareTo(other: Price): number {
    if(other.currency !== this.currency) {
      throw new IllegalOperationException('You cannot add two prices with different currencies');
    }

    if(this._value < other._value) return -1;
    else if(this._value > other._value) return 1;
    else return 0;
  }

  // Adds this Price and [other] and returns the sum as a new Price object.
  // Throws [IllegalOperationException] if [other] is in a different currency.
  add(other: Price | null | undefined): Price {
    if(other == null) {
      return Price.withCurrencyFromUnits(this._currency, this._value);
    }

    if(other.currency !== this.currency) {
      throw new IllegalOperationException('You cannot add two prices with different currencies');
    }

    return Price.withCurrencyFromUnits(this._currency, this._value + other._value);
  }

  // Returns the price difference between this Price and [other] as a new Price object.
  // Example:
  //    If [this] is $50 and [other] is $20, the result will be $30.
  //    If [this] is $20 and [other] is $50, the result will be $-30.
  // Throws [IllegalOperationException] if [other] is in a different currency.
  difference(other: Price | null | undefined): Price {
    if(other == null) {
      return Price.withCurrencyFromUnits(this._currency, this._value);
    }

    if(other.currency !== this.currency) {
      throw new IllegalOperationException('You cannot add two prices with different currencies');
    }

    return Price.withCurrencyFromUnits(this._currency, this._value - other._value);
  }

  // Negates the price(e.g. $50 will become $-50).
  negative(): Price {
    return new Price(this._currency, -this._value, this._coins);
  }

  scale(scalar: number): Price {
    const newValue = DartMath.round(this._value * scalar);
    return Price.withCurrencyFromUnits(this.currency, newValue);
  }

  exchange(currency: Currency, rate: number): Price {
    const newValue = DartMath.toInt(this._value * rate);
    return Price.withCurrencyFromUnits(currency, newValue);
  }

  isSame(other: Price): boolean {
    if(this === other) {
      return true;
    }

    return other._value === this._value &&
          other._currency === this._currency;
  }

  toString(): string {
    return this.encode();
  }

  isNegative(): boolean {
    return this.value < 0;
  }

  isPositive(): boolean {
    return this.value > 0;
  }

  isZero(): boolean {
    return !this.isNegative() && !this.isPositive();
  }

  static max(lh: Price, rh: Price): Price {
    if(lh.currency !== rh.currency) {
      throw new IllegalOperationException('You cannot compare two prices with different currencies');
    }

    return Price.withCurrencyFromUnits(lh._currency, Math.max(lh._value, rh._value));
  }

  static min(lh: Price, rh: Price): Price {
    if(lh.currency !== rh.currency) {
      throw new IllegalOperationException('You cannot compare two prices with different currencies');
    }

    return Price.withCurrencyFromUnits(lh._currency, Math.min(lh._value, rh._value));
  }

  static calculateNetAndVatPriceBreakdown({ grossUnitPrice, quantity, vatRatio }: {
    grossUnitPrice: Price,
    quantity: number,
    vatRatio: number,
  }): { netUnitPrice: Price, netTotalPrice: Price, vatTotalPrice: Price } {
    const grossTotalPriceUnits = grossUnitPrice.value * quantity;
    const netUnitUnits = grossUnitPrice.value * (1.0 / (1.0 + vatRatio));
    const netTotalUnits = netUnitUnits * quantity;
    const vatTotalUnits = grossTotalPriceUnits - netTotalUnits;

    const coins = getCoinsPerCurrencyUnit(grossUnitPrice.currency);

    return {
      netUnitPrice: new Price(grossUnitPrice.currency, netUnitUnits, coins, true),
      netTotalPrice: new Price(grossUnitPrice.currency, netTotalUnits, coins, true),
      vatTotalPrice: new Price(grossUnitPrice.currency, vatTotalUnits, coins, true),
    };
  }
}

const coinsPerCurrencyUnit = new Map<Currency, number>([
  [Currency.USD, 100],
  [Currency.EUR, 100],
  [Currency.RON, 100],
  [Currency.HUF, 100],
]);

export function getCoinsPerCurrencyUnit(currency: Currency): number {
  const result = coinsPerCurrencyUnit.get(currency);
  if(isNotNullOrUndefined(result)) {
    return result;
  }

  throw new Error(`Failed to get coins per currency for currency: ${currency}`);
}
